import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface PolicyVersion {
  policy_type: string;
  version: string;
  summary_of_changes: string | null;
  effective_date: string;
}

interface ConsentStatus {
  needsReconfirmation: boolean;
  outdatedPolicies: PolicyVersion[];
  loading: boolean;
}

export function useConsentReconfirmation(userId: string | undefined) {
  const [status, setStatus] = useState<ConsentStatus>({
    needsReconfirmation: false,
    outdatedPolicies: [],
    loading: true,
  });

  useEffect(() => {
    if (!userId) {
      setStatus({ needsReconfirmation: false, outdatedPolicies: [], loading: false });
      return;
    }

    checkConsentStatus();
  }, [userId]);

  const checkConsentStatus = async () => {
    if (!userId) return;

    try {
      // Get current policy versions
      const { data: policyVersions, error: policyError } = await supabase
        .from('policy_versions')
        .select('*');

      if (policyError) throw policyError;

      // Get user's consent records for terms and privacy
      const { data: consents, error: consentError } = await supabase
        .from('user_consents')
        .select('*')
        .eq('user_id', userId)
        .in('consent_type', ['terms_and_conditions', 'privacy_policy']);

      if (consentError) throw consentError;

      // Check which policies need re-confirmation
      const outdatedPolicies: PolicyVersion[] = [];

      for (const policy of policyVersions || []) {
        const userConsent = consents?.find(
          (c) => c.consent_type === policy.policy_type
        );

        // Need re-confirmation if:
        // 1. No consent record exists
        // 2. Consent version is older than current policy version
        // 3. Consent has been withdrawn
        if (
          !userConsent ||
          userConsent.version_consented_to < policy.version ||
          userConsent.withdrawn_at
        ) {
          outdatedPolicies.push(policy);
        }
      }

      setStatus({
        needsReconfirmation: outdatedPolicies.length > 0,
        outdatedPolicies,
        loading: false,
      });
    } catch (error) {
      console.error('Error checking consent status:', error);
      setStatus({ needsReconfirmation: false, outdatedPolicies: [], loading: false });
    }
  };

  const confirmConsent = async (policyTypes: string[]) => {
    if (!userId) return false;

    try {
      const userAgent = navigator.userAgent;
      const now = new Date().toISOString();

      // Get current versions
      const { data: policyVersions } = await supabase
        .from('policy_versions')
        .select('*')
        .in('policy_type', policyTypes);

      for (const policy of policyVersions || []) {
        // Upsert consent record
        const { error } = await supabase
          .from('user_consents')
          .upsert({
            user_id: userId,
            consent_type: policy.policy_type,
            consented: true,
            consented_at: now,
            version_consented_to: policy.version,
            user_agent: userAgent,
            withdrawn_at: null,
          }, {
            onConflict: 'user_id,consent_type',
          });

        if (error) throw error;
      }

      // Refresh status
      await checkConsentStatus();
      return true;
    } catch (error) {
      console.error('Error confirming consent:', error);
      return false;
    }
  };

  return { ...status, confirmConsent, refreshStatus: checkConsentStatus };
}
